/**
 * @fileoverview Resolve current route's package name for footer version display.
 * @purpose Map pathname to @korvo package using `korvo.routes.lock.json` page entries (first URL segment → owning package).
 */

import routesLock from '../../korvo.routes.lock.json';

type LockFiles = Record<string, { source?: string }>;

/** First path segment (after /) → npm package short name (no @korvo/ prefix). Longest-prefix match not needed — one segment. */
function buildSegmentToPackage(lock: { files?: LockFiles }): Map<string, string> {
  const map = new Map<string, string>();
  const files = lock.files;
  if (!files || typeof files !== 'object') {
    return map;
  }
  for (const [relKey, meta] of Object.entries(files)) {
    const key = relKey.replace(/\\/g, '/');
    if (!key.startsWith('src/app/') || !key.endsWith('/page.tsx')) {
      continue;
    }
    const inner = key.slice('src/app/'.length, -'/page.tsx'.length);
    const firstSeg = inner.split('/')[0];
    if (firstSeg === '' || firstSeg.startsWith('(') || firstSeg === 'api') {
      continue;
    }
    const source = meta?.source;
    if (typeof source !== 'string' || !source.startsWith('@korvo/')) {
      continue;
    }
    const pkg = source.slice('@korvo/'.length);
    map.set(firstSeg, pkg);
  }
  return map;
}

const SEGMENT_TO_PACKAGE = buildSegmentToPackage(routesLock as { files?: LockFiles });

/**
 * First URL segments for app-local pages that delegate to shell but are not korvo-codegen stubs
 * (see `src/app/workbench/[id]/page.tsx` — not listed in `korvo.routes.lock.json`).
 */
const SHELL_DELEGATED_APP_SEGMENTS = new Map<string, string>([['workbench', 'shell']]);

/**
 * Returns the package name (e.g. editor, chat) for the given pathname for footer display.
 */
export function getPackageForPathname(pathname: string | null): string {
  if (!pathname) {
    return 'app';
  }
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const segments = normalized.split('/').filter((s) => s.length > 0);
  const first = segments[0];
  if (first === undefined) {
    return 'app';
  }
  const pkg = SEGMENT_TO_PACKAGE.get(first);
  if (pkg !== undefined) {
    return pkg;
  }
  const delegated = SHELL_DELEGATED_APP_SEGMENTS.get(first);
  if (delegated !== undefined) {
    return delegated;
  }
  return 'app';
}
